import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { PhoneIcon } from '@rsos/assets/icons';
import { Box, Text } from '@rsos/components/capstone/base';
import { CALL_OUT_OF_JURISDICTION } from '@rsos/constants/collaborator';
import { useGetSelectedCollaborator } from '@rsos/reduxDataStore/src/features/emergencyCalls/helpers';
import { OVERVIEW_TAB } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneTabs';
import { useSelectedTextFrom911Conversation } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Selectors';
import { TF911_CONVERSATION_ENDED } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Slice';
import useDisplayPhoneNumber from '../../../../hooks/useDisplayPhoneNumber';
import useHasAccessToUniteEventPane from '../../../../hooks/useHasAccessToUniteEventPane';
import { CollapseButton } from '../SharedChatUI/CollapseButton';
import { TranslationMenu } from '../SharedChatUI/TranslationMenu';
import { TranslationMenuButton } from '../SharedChatUI/TranslationMenuButton';
import { ActionsBox, HeaderContainer, RedCircle } from './TF911.styles';
import TF911ChatHeaderButtons from './TF911ChatHeaderButtons';

/**
 * Displays a chat header above the chat box.
 * @param {Array} callChatParticipants - list of chat participants for 911 calls
 * @param {Boolean} isForOverview - Flag for checking if the component is used for the overview tab
 * @param {Function} onClickShowAccordion - Onclick handler for toggling the accordion for preconfigured messages
 * @param {Function} onStartConversation - Onclick handler for starting the conversation
 * @param {Boolean} showStartChat - Flag for showing the Start Chat button
 * @param {Boolean} showPreconfiguredMessages - Flag for showing preconfigured messages to start the chat with
 * @param {Boolean} isOutboundSMSConversation - Flag for if conversation is from OutboundSMS
 */
const TF911ChatHeader = ({
  isForOverview = false,
  onClickShowAccordion,
  onStartConversation,
  showStartChat,
  showPreconfiguredMessages,
  gpxFeature,
}) => {
  const hasAccessToUniteEventPane = useHasAccessToUniteEventPane();

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );

  const collaborator = useGetSelectedCollaborator();
  const isOutOfJurisdiction = collaborator.type === CALL_OUT_OF_JURISDICTION;

  const conversationChatHeader = useMemo(() => {
    if (collaborator.type) {
      if (isOutOfJurisdiction) {
        return 'Citizen Live Chat - Read Only';
      }
      return 'Citizen Live Chat';
    }
    return 'Live Chat';
  }, [collaborator.type, isOutOfJurisdiction]);

  const displayPhoneNumber = useDisplayPhoneNumber(selectedCallerID);

  const conversation = useSelectedTextFrom911Conversation(selectedCallerID);

  const isFetchingMetadata = useMemo(
    () => conversation?.loading?.getConversationMetadata,
    [conversation],
  );

  const isCreatingConversation = useMemo(
    () => conversation?.loading?.createConversation,
    [conversation],
  );

  const isConversationEnded = useMemo(
    () => conversation?.data?.custom?.STATUS === TF911_CONVERSATION_ENDED,
    [conversation],
  );

  const onOverviewTab =
    useSelector(state => state.eventPane?.selectedTab === OVERVIEW_TAB) &&
    hasAccessToUniteEventPane;

  return (
    <HeaderContainer
      data-name="text-from-911-chat-header"
      p={isForOverview ? '16px' : '12px'}
    >
      <Box horizontal justify="space-between" width="100%">
        <Box>
          <Text inline fontWeight="bold">
            <Text color="primaryText" size="normal">
              {!isFetchingMetadata &&
                conversation?.data?.conversation_id &&
                !isConversationEnded && <RedCircle />}
              {!isFetchingMetadata &&
                (!isConversationEnded ? conversationChatHeader : 'Closed Chat')}
            </Text>
            <Box horizontal>
              {!hasAccessToUniteEventPane && (
                <Text color="secondaryText">
                  <PhoneIcon
                    width={10}
                    height={12}
                    viewboxWidth={11}
                    viewboxHeight={15}
                  />
                </Text>
              )}
              <Text
                color="secondaryText"
                fontWeight={hasAccessToUniteEventPane ? 'regular' : 'inherit'}
                size={hasAccessToUniteEventPane ? 'xsmall' : 'small'}
              >
                {displayPhoneNumber}
              </Text>
            </Box>
          </Text>
        </Box>
        {!onOverviewTab && (
          <ActionsBox>
            {!showStartChat && <TranslationMenuButton />}
            <CollapseButton />
          </ActionsBox>
        )}
      </Box>
      {!onOverviewTab && !showStartChat && <TranslationMenu />}
      {!isOutOfJurisdiction && (
        <TF911ChatHeaderButtons
          isFetchingMetadata={isFetchingMetadata}
          showStartChat={showStartChat}
          isCreatingConversation={isCreatingConversation}
          hasAccessToUniteEventPane={hasAccessToUniteEventPane}
          isForOverview={isForOverview}
          onClickShowAccordion={onClickShowAccordion}
          onStartConversation={onStartConversation}
          selectedCallerID={selectedCallerID}
          showPreconfiguredMessages={showPreconfiguredMessages}
          gpxFeature={gpxFeature}
        />
      )}
    </HeaderContainer>
  );
};

TF911ChatHeader.propTypes = {
  callParticipants: PropTypes.array,
  isChatBoxVisible: PropTypes.bool,
  isForOverview: PropTypes.bool,
  onActionButtonClick: PropTypes.func,
  onClickShowAccordion: PropTypes.func,
  onEndConversation: PropTypes.func,
  onStartConversation: PropTypes.func,
  showStartChat: PropTypes.bool,
  showPreconfiguredMessages: PropTypes.bool,
  toggleChatBoxVisibility: PropTypes.func,
};

export default TF911ChatHeader;
